import { View, Text, TextInput } from 'react-native';
import React, { useEffect, useState, useCallback } from 'react';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Image } from 'expo-image';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';
import usePlatform from '@/utils/hooks/usePlatform';
import useLoader from '@/utils/hooks/useLoader';
import { getOrganization, getCurrentOrgID } from '@/data/organization';
import { supabase } from '@/data/client';
import SolidButton from '@/components/General/SolidButton';

const formatCurrency = (cents, currencyCode = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode, minimumFractionDigits: 2 }).format(cents / 100);

const formatMethodDetail = (detail) => {
    if (!detail) return 'Online Gift';
    const { methodType, cardBrand, cardLast4, achBankName, achAccountNumberLast4, applePay } = detail;
    if (methodType === 'APPLE_PAY') {
        return `Apple Pay — ${applePay?.description || 'card'}`;
    }
    if (methodType === 'CARD' || cardBrand) {
        return `${cardBrand || 'Card'} •••• ${cardLast4 || ''}`;
    }
    if (methodType === 'ACH' || achBankName) {
        return `${achBankName || 'Bank'} •••• ${achAccountNumberLast4 || ''}`;
    }
    return 'Online Gift';
};

/**
 * GivingReceipt — visible receipt card shown on the thank-you page.
 *
 * Props:
 *   receiptData  – { payinID, amountCents, methodType, fundSplits, note, billingContact, card, ach, applePay, date }
 *   organizationID – org ID for fetching org details
 *   personEmail   – logged-in user email for autofill (optional)
 *   onEmailSent   – callback after email is sent
 */
export default function GivingReceipt({ receiptData, organizationID, personEmail, onEmailSent }) {
    const { theme: { colors, scheme } } = useUnistyles();
    const { isWeb } = usePlatform();
    const loader = useLoader();
    const [org, setOrg] = useState(null);
    const [orgLogoBase64, setOrgLogoBase64] = useState(null);
    const [emailAddress, setEmailAddress] = useState(personEmail || '');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);

    useEffect(() => {
        const loadOrg = async () => {
            try {
                const orgID = organizationID || await getCurrentOrgID();
                const orgData = await getOrganization(orgID);
                setOrg(orgData);
                if (orgData?.logoPublicUrlLongDark) {
                    try {
                        const response = await fetch(orgData.logoPublicUrlLongDark);
                        const blob = await response.blob();
                        const base64 = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        setOrgLogoBase64(base64);
                    } catch {
                        // Logo failed to load
                    }
                }
            } catch {
                // Org load failed
            }
        };
        loadOrg();
    }, [organizationID]);

    const generateHTML = useCallback(() => {
        const {
            payinID, amountCents, convenienceFeeCents = 0, grossAmountCents, currencyCode = 'USD', methodType, fundSplits = [], note,
            billingContact = {}, card = {}, ach = {}, applePay, date,
        } = receiptData;

        const txDate = date ? DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED) : DateTime.now().toLocaleString(DateTime.DATETIME_MED);
        const donorName = billingContact.name || '';
        const donorEmail = billingContact.email || '';
        const methodDetail = formatMethodDetail({ methodType, cardBrand: card?.brand_desc || card?.brand, cardLast4: card?.last_4, achBankName: ach?.bank_name, achAccountNumberLast4: ach?.account_number_last_4, applePay: applePay ?? card });

        const fundRows = fundSplits.map((split) => {
            const fundName = split.fundName || split.fundID || 'General';
            const amount = split.amountCents != null ? split.amountCents : split.amount;
            return `<tr><td style="padding:8px 0;border-bottom:1px solid #eee;">${fundName}</td><td style="padding:8px 0;border-bottom:1px solid #eee;text-align:right;font-weight:600;">${formatCurrency(amount, currencyCode)}</td></tr>`;
        }).join('');

        const totalCharged = grossAmountCents ?? (amountCents + convenienceFeeCents);
        const totalRow = `<tr><td style="padding:8px 0;">Gift amount</td><td style="padding:8px 0;text-align:right;">${formatCurrency(amountCents, currencyCode)}</td></tr><tr><td style="padding:8px 0;">Convenience fee</td><td style="padding:8px 0;text-align:right;">${formatCurrency(convenienceFeeCents, currencyCode)}</td></tr><tr><td style="padding:12px 0;font-weight:700;font-size:16px;">Total charged</td><td style="padding:12px 0;text-align:right;font-weight:700;font-size:16px;color:#059669;">${formatCurrency(totalCharged, currencyCode)}</td></tr>`;

        const orgAddressHtml = (() => {
            const a = org?.Address;
            if (!a) return '';
            return `<div style="font-size:12px;color:#6b7280;margin-top:4px;">${a.street}${a.streetLine2 ? `, ${a.streetLine2}` : ''} · ${a.city}, ${a.state} ${a.zip}</div>`;
        })();

        const logoHtml = orgLogoBase64
            ? `<img src="${orgLogoBase64}" style="max-height:50px;max-width:220px;object-fit:contain;margin-bottom:8px;display:block;" />`
            : `<div style="font-size:22px;font-weight:700;color:#1f2937;margin-bottom:8px;">${org?.name || ''}</div>`;

        const noteHtml = note ? `<div style="margin-top:16px;padding:12px;background:#f9fafb;border-radius:8px;"><div style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;font-weight:600;margin-bottom:4px;">Note</div><div style="font-size:13px;color:#4b5563;">${note}</div></div>` : '';

        const donorInfoHtml = donorName || donorEmail
            ? `<div style="margin-top:4px;font-size:13px;color:#4b5563;">${donorName}${donorEmail ? `<br>${donorEmail}` : ''}</div>`
            : '';

        return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Giving Receipt – ${payinID}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;line-height:1.5;color:#1f2937;background:#f5f5f5;padding:20px}.page{max-width:520px;margin:0 auto;background:white;padding:40px;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,0.08)}.header{padding-bottom:20px;border-bottom:2px solid #059669;margin-bottom:24px}.receipt-title{font-size:20px;font-weight:700;color:#059669;margin-top:8px}.receipt-subtitle{font-size:12px;color:#9ca3af;margin-top:4px}.section-label{font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;font-weight:600;margin-bottom:6px;margin-top:16px}table{width:100%;border-collapse:collapse}.tx-id{font-size:11px;color:#9ca3af;font-family:monospace;margin-top:12px;word-break:break-all}.footer{margin-top:24px;padding-top:16px;border-top:1px solid #eee;font-size:11px;color:#9ca3af;text-align:center}</style></head><body><div class="page"><div class="header">${logoHtml}${orgAddressHtml}<div class="receipt-title">Giving Receipt</div><div class="receipt-subtitle">${txDate}</div></div><div class="section-label">Donor</div>${donorInfoHtml || '<div style="font-size:13px;color:#9ca3af;">Guest</div>'}<div class="section-label">Payment Method</div><div style="font-size:13px;color:#4b5563;">${methodDetail}</div><div class="section-label">Fund Allocation</div><table>${fundRows}${totalRow}</table>${noteHtml}<div class="tx-id">Transaction ID: ${payinID}</div><div class="footer">Thank you for your generous gift.<br>${org?.name || ''}</div></div></body></html>`;
    }, [receiptData, org, orgLogoBase64]);

    const handleDownloadPDF = useCallback(async () => {
        const fileName = `Reach_Giving_Receipt_${receiptData?.payinID || 'receipt'}`;

        if (isWeb) {
            const html = generateHTML();
            const printWindow = window.open('', '_blank');
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
            }, 500);
        } else {
            try {
                loader.enable();
                const html = generateHTML();
                let RNHTMLtoPDF;
                try {
                    RNHTMLtoPDF = await import('react-native-html-to-pdf');
                } catch {
                    throw new Error('PDF library not available');
                }
                const { shareAsync } = await import('expo-sharing');
                const file = await RNHTMLtoPDF.generatePDF({ html, fileName, directory: 'Documents' });
                loader.disable();
                if (file?.filePath) {
                    try {
                        await shareAsync(file.filePath, { mimeType: 'application/pdf', dialogTitle: 'Save Gift Receipt' });
                    } catch {
                        // User cancelled share
                    }
                }
                Toast.show({ type: 'success', text2: 'Receipt saved' });
            } catch (error) {
                loader.disable();
                Toast.show({ type: 'error', text2: 'Failed to save receipt: ' + error.message });
            }
        }
    }, [generateHTML, isWeb, loader, receiptData]);

    const handleEmailReceipt = useCallback(async () => {
        if (!emailAddress?.trim()) {
            Toast.show({ type: 'error', text2: 'Enter an email address' });
            return;
        }
        if (!receiptData?.payinID || receiptData.payinID === 'N/A') {
            Toast.show({ type: 'error', text2: 'Receipt unavailable' });
            return;
        }
        setSendingEmail(true);
        try {
            const { error } = await supabase.functions.invoke('send-giving-receipt', {
                body: {
                    organizationID,
                    emailAddress: emailAddress.trim(),
                    payinID: receiptData.payinID,
                },
            });
            if (error) throw error;
            Toast.show({ type: 'success', text2: 'Receipt emailed' });
            setShowEmailInput(false);
            onEmailSent?.();
        } catch (error) {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to email receipt' });
        } finally {
            setSendingEmail(false);
        }
    }, [emailAddress, organizationID, receiptData, onEmailSent]);

    const {
        payinID, amountCents, convenienceFeeCents = 0, grossAmountCents, currencyCode = 'USD', methodType, fundSplits = [], note,
        billingContact = {}, card = {}, ach = {}, applePay, date,
    } = receiptData || {};

    const txDate = date ? DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED) : DateTime.now().toLocaleString(DateTime.DATETIME_MED);
    const methodDetailStr = formatMethodDetail({ methodType, cardBrand: card?.brand_desc || card?.brand, cardLast4: card?.last_4, achBankName: ach?.bank_name, achAccountNumberLast4: ach?.account_number_last_4, applePay: applePay ?? card });

    return (
        <View style={styles.container} testID="giving-receipt">
            {/* Org logo */}
            {org?.logoPublicUrlLong && (
                <Image
                    source={scheme === 'light'
                        ? { uri: org.logoPublicUrlLongDark }
                        : { uri: org.logoPublicUrlLong }}
                    style={styles.orgLogo}
                    contentFit="contain"
                    transition={250}
                />
            )}

            <Text style={styles.receiptTitle}>Giving Receipt</Text>
            <Text style={styles.receiptDate}>{txDate}</Text>

            {/* Donor */}
            <View style={styles.section}>
                <Text style={styles.sectionLabel}>Donor</Text>
                <Text style={styles.sectionValue}>
                    {billingContact.name || 'Guest'}
                    {billingContact.email ? `\n${billingContact.email}` : ''}
                </Text>
            </View>

            {/* Payment method */}
            <View style={styles.section}>
                <Text style={styles.sectionLabel}>Payment Method</Text>
                <Text style={styles.sectionValue}>{methodDetailStr}</Text>
            </View>

            {/* Fund allocation */}
            <View style={styles.section}>
                <Text style={styles.sectionLabel}>Fund Allocation</Text>
                {fundSplits.map((split, i) => (
                    <View key={i} style={styles.fundRow}>
                        <Text style={styles.fundName}>{split.fundName || split.fundID || 'General'}</Text>
                        <Text style={styles.fundAmount}>{formatCurrency(split.amountCents != null ? split.amountCents : split.amount, currencyCode)}</Text>
                    </View>
                ))}
                <View style={styles.summaryRow} testID="receipt-gift-amount">
                    <Text style={styles.summaryLabel}>Gift amount</Text>
                    <Text style={styles.summaryAmount}>{formatCurrency(amountCents, currencyCode)}</Text>
                </View>
                <View style={styles.summaryRow} testID="receipt-convenience-fee">
                    <Text style={styles.summaryLabel}>Convenience fee</Text>
                    <Text style={styles.summaryAmount}>{formatCurrency(convenienceFeeCents, currencyCode)}</Text>
                </View>
                <View style={styles.totalRow} testID="receipt-total-charged">
                    <Text style={styles.totalLabel}>Total charged</Text>
                    <Text style={styles.totalAmount}>{formatCurrency(grossAmountCents ?? (amountCents + convenienceFeeCents), currencyCode)}</Text>
                </View>
            </View>

            {/* Note */}
            {note ? (
                <View style={styles.section}>
                    <Text style={styles.sectionLabel}>Note</Text>
                    <Text style={styles.noteText}>{note}</Text>
                </View>
            ) : null}

            {/* Transaction ID */}
            <Text style={styles.txId}>Transaction ID: {payinID}</Text>

            {/* Actions */}
            <View style={styles.actions}>
                <SolidButton
                    testID="receipt-download"
                    text="Save Receipt"
                    iconName="download-outline"
                    type="giving"
                    variant="muted"
                    size="sm"
                    onPress={handleDownloadPDF}
                />
                <SolidButton
                    testID="receipt-email-toggle"
                    text="Email Receipt"
                    iconName="mail-outline"
                    type="giving"
                    variant="muted"
                    size="sm"
                    onPress={() => setShowEmailInput(!showEmailInput)}
                />
            </View>

            {showEmailInput && (
                <View style={styles.emailSection}>
                    <TextInput
                        testID="receipt-email-input"
                        style={styles.emailInput}
                        placeholder="Email address"
                        placeholderTextColor={colors.textTertiary}
                        value={emailAddress}
                        onChangeText={setEmailAddress}
                        keyboardType="email-address"
                        autoCapitalize="none"
                    />
                    <SolidButton
                        testID="receipt-email-send"
                        text={sendingEmail ? 'Sending...' : 'Send'}
                        type="giving"
                        size="sm"
                        disabled={sendingEmail}
                        onPress={handleEmailReceipt}
                    />
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography, shadows }) => ({
    container: {
        backgroundColor: colors.surface,
        borderRadius: roundness.md,
        padding: spacing.lg,
        gap: spacing.sm,
        boxShadow: shadows.card,
        width: '100%',
    },
    orgLogo: {
        width: '100%',
        height: 50,
        marginBottom: spacing.sm,
    },
    receiptTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.giving,
    },
    receiptDate: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    section: {
        gap: 2,
    },
    sectionLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    sectionValue: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    fundRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    fundName: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    fundAmount: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: spacing.xs,
    },
    summaryLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    summaryAmount: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: spacing.sm,
        marginTop: 4,
    },
    totalLabel: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    totalAmount: {
        fontSize: typography.h3,
        fontFamily: fonts.bold,
        color: colors.giving,
    },
    noteText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    txId: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        marginTop: spacing.xs,
    },
    actions: {
        flexDirection: 'row',
        gap: spacing.sm,
        marginTop: spacing.md,
    },
    emailSection: {
        flexDirection: 'row',
        gap: spacing.sm,
        marginTop: spacing.sm,
        alignItems: 'center',
    },
    emailInput: {
        flex: 1,
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.body,
        padding: spacing.sm,
    },
}));
